(function () {
  'use strict';

  if (window.DieuxFrame) return;

  const reads = [];
  const writes = [];
  const keyedReads = new Map();
  let frame;

  function schedule() {
    if (frame) return;
    frame = window.requestAnimationFrame(flush);
  }

  function run(task) {
    try {
      task();
    } catch (error) {
      window.reportError?.(error);
    }
  }

  function flush() {
    frame = undefined;

    const pendingReads = reads.splice(0, reads.length);
    const pendingKeyedReads = Array.from(keyedReads.values());
    keyedReads.clear();

    pendingReads.forEach(run);
    pendingKeyedReads.forEach(run);

    writes.splice(0, writes.length).forEach(run);

    if (reads.length || writes.length || keyedReads.size) schedule();
  }

  /**
   * Queues a layout read for the shared measurement phase.
   *
   * @param {() => void} task - Callback that only reads geometry or style.
   * @returns {void}
   */
  function read(task) {
    if (typeof task !== 'function') return;
    reads.push(task);
    schedule();
  }

  /**
   * Queues a layout write for the shared mutation phase.
   *
   * @param {() => void} task - Callback that only writes to the DOM.
   * @returns {void}
   */
  function write(task) {
    if (typeof task !== 'function') return;
    writes.push(task);
    schedule();
  }

  /**
   * Queues a coalescing layout read.
   *
   * @param {unknown} key - Identity of the measurement, unique per owner and purpose.
   * @param {() => void} task - Callback that only reads geometry or style.
   * @returns {void}
   */
  function measure(key, task) {
    if (typeof task !== 'function') return;
    if (key === undefined || key === null) {
      read(task);
      return;
    }
    keyedReads.set(key, task);
    schedule();
  }

  /**
   * Drops a pending coalescing read, for owners tearing down before their
   * frame runs.
   *
   * @param {unknown} key - Key previously passed to `measure`.
   * @returns {void}
   */
  function cancelMeasure(key) {
    keyedReads.delete(key);
  }

  const lengthMemo = new Map();
  const RELATIVE_LENGTH = /^(-?(?:\d+\.?\d*|\.\d+))(px|rem|em)$/;
  let probe;

  function fontSizeOf(element) {
    return Number.parseFloat(window.getComputedStyle(element).fontSize) || 16;
  }

  function ensureProbe() {
    if (probe?.isConnected) return probe;

    probe = document.createElement('div');
    probe.setAttribute('aria-hidden', 'true');
    // Absolutely positioned, zero block-size and contained, so the probe can
    // neither shift the page nor be shifted by it.
    probe.style.cssText =
      'position:absolute;top:0;left:0;block-size:0;visibility:hidden;pointer-events:none;contain:layout style;';
    document.body.append(probe);
    return probe;
  }

  /**
   * Resolves a CSS length token to pixels.
   *
   * @param {string} token - CSS length, typically a custom property's computed value.
   * @param {Element} [contextElement] - Element whose font-size scopes `em`. Defaults to the root element.
   * @returns {number} Resolved pixel length, or `0` when the token is empty or unresolvable.
   */
  function resolveLength(token, contextElement) {
    const value = typeof token === 'string' ? token.trim() : '';
    if (!value) return 0;

    const context = contextElement instanceof Element ? contextElement : document.documentElement;
    const rootFontSize = fontSizeOf(document.documentElement);

    const relative = RELATIVE_LENGTH.exec(value);
    if (relative) {
      const amount = Number.parseFloat(relative[1]);
      if (relative[2] === 'px') return amount;
      if (relative[2] === 'rem') return amount * rootFontSize;
      return amount * fontSizeOf(context);
    }

    const contextFontSize = fontSizeOf(context);
    const memoKey = `${value}|${contextFontSize}|${rootFontSize}|${window.innerWidth}`;
    const memoized = lengthMemo.get(memoKey);
    if (memoized !== undefined) return memoized;

    const element = ensureProbe();
    element.style.fontSize = `${contextFontSize}px`;
    element.style.inlineSize = value;
    const resolved = element.getBoundingClientRect().width;
    const length = Number.isFinite(resolved) ? resolved : 0;

    lengthMemo.set(memoKey, length);
    return length;
  }

  /**
   * Resolves a custom property on an element to pixels through `resolveLength`.
   *
   * @param {Element} element - Element whose computed style carries the property.
   * @param {string} property - Custom property name, including the leading `--`.
   * @returns {number} Resolved pixel length, or `0` when unset or unresolvable.
   */
  function resolveCustomProperty(element, property) {
    if (!(element instanceof Element) || typeof property !== 'string') return 0;

    const token = window.getComputedStyle(element).getPropertyValue(property);
    return resolveLength(token, element);
  }

  const TAIL_CLEARANCE_PX = 2;

  /**
   * Reserves the trailing scroll a document needs for its last anchor target to reach the top of a scroll-spy's tracking region.
   *
   * @param {object} options - Reservation to measure.
   * @param {Element} options.target - Last tracked element, the one that must be able to reach the line.
   * @param {number} options.line - Offset in pixels from the top of the viewport where the tracking region starts.
   * @param {Element} options.host - Element the custom property is written to and read back from. Typically the owner's root, leaving the stylesheet free to apply the inherited value on whichever descendant closes the document.
   * @param {string} options.property - Custom property name, including the leading `--`.
   * @param {number} [options.clearance] - Pixels of overlap past the line.
   * @returns {number} Room now reserved, in pixels.
   */
  function reserveTailRoom(options) {
    const { target, line, host, property, clearance = TAIL_CLEARANCE_PX } = options || {};
    if (!(target instanceof Element) || !(host instanceof Element)) return 0;
    if (typeof property !== 'string' || !property.startsWith('--')) return 0;

    const doc = document.documentElement;
    const applied = resolveCustomProperty(host, property);
    const targetTop = target.getBoundingClientRect().top + window.scrollY;
    const trailing = doc.scrollHeight - targetTop - applied;
    const needed = doc.clientHeight - (Number.isFinite(line) ? line : 0) + clearance;
    const room = Math.max(0, Math.ceil(needed - trailing));
    if (room === applied) return room;

    write(() => {
      if (host.isConnected) host.style.setProperty(property, `${room}px`);
    });

    return room;
  }

  /**
   * Runs a task once the current frame has been presented.
   *
   * @param {() => void} task - Callback that may read or write freely.
   * @returns {void}
   */
  function afterPaint(task) {
    if (typeof task !== 'function') return;
    window.requestAnimationFrame(() => window.requestAnimationFrame(() => run(task)));
  }

  const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  /**
   * Whether the visitor has asked for reduced motion right now.
   *
   * @returns {boolean} True when the reduce preference is set.
   */
  function prefersReducedMotion() {
    return motionQuery.matches;
  }

  const focusableSelector = [
    'a[href]',
    'area[href]',
    'button',
    'input:not([type="hidden"])',
    'select',
    'textarea',
    'iframe',
    'summary',
    'video[controls]',
    'audio[controls]',
    '[tabindex]',
    '[contenteditable]:not([contenteditable="false"])',
  ].join(', ');

  const tabbableSelector = `:is(${focusableSelector}):not([tabindex^="-"], :disabled)`;

  function isUnreachable(element, scope) {
    const blocker = element.closest('[hidden], [inert]');
    if (blocker && blocker !== scope && (!scope || scope.contains(blocker))) return true;

    let details = element.parentElement?.closest('details:not([open])');
    while (details && details !== scope && (!scope || scope.contains(details))) {
      if (!details.querySelector(':scope > summary')?.contains(element)) return true;
      details = details.parentElement?.closest('details:not([open])');
    }
    return false;
  }

  function isVisible(element) {
    return typeof element.checkVisibility === 'function'
      ? element.checkVisibility({ visibilityProperty: true })
      : element.getClientRects().length > 0;
  }

  /**
   * Whether Tab can reach an element.
   *
   * @param {Element} element - Candidate.
   * @param {{scope?: Element, visible?: boolean}} [options] - `hidden`, `inert` and closed `<details>` at or above `scope` are ignored; `visible` also requires a visible box and reads layout.
   * @returns {boolean} Whether the element is a sequential focus stop.
   */
  function isTabbable(element, options) {
    if (!(element instanceof Element) || !element.matches(tabbableSelector)) return false;

    const { scope, visible = false } = options || {};
    if (isUnreachable(element, scope instanceof Element ? scope : undefined)) return false;
    return !visible || isVisible(element);
  }

  /**
   * Whether a container holds anything Tab can reach, including the built-in summary of a `<details>` that has none authored.
   *
   * @param {Element} root - Container to search; its own `hidden`/`inert` state is ignored.
   * @param {{visible?: boolean}} [options] - `visible` also requires a visible box while `root` itself renders, and reads layout.
   * @returns {boolean} Whether a descendant is a sequential focus stop.
   */
  function hasTabbable(root, options) {
    if (!(root instanceof Element)) return false;

    const visible = Boolean(options?.visible) && root.getClientRects().length > 0;
    for (const element of root.querySelectorAll(tabbableSelector)) {
      if (isTabbable(element, { scope: root, visible })) return true;
    }
    for (const details of root.querySelectorAll('details')) {
      if (details.querySelector(':scope > summary') || isUnreachable(details, root)) continue;
      if (!visible || isVisible(details)) return true;
    }
    return false;
  }

  /**
   * Keeps `tabindex="0"` on each target only while it needs a Tab stop of its own, read in the measure phase and written in the write phase.
   *
   * @param {unknown} key - Coalescing key, unique per owner; pass it to `cancelMeasure` on teardown.
   * @param {object} options - Stop rule.
   * @param {() => Iterable<Element>} options.targets - Elements that may carry the stop, resolved in both phases.
   * @param {(target: Element) => boolean} options.needsStop - Pure read deciding one target's stop.
   * @param {() => boolean} [options.isCurrent] - Checked before each phase; false drops the pass.
   * @returns {void}
   */
  function syncTabStops(key, options) {
    const { targets, needsStop, isCurrent } = options || {};
    if (typeof targets !== 'function' || typeof needsStop !== 'function') return;
    const current = () => typeof isCurrent !== 'function' || Boolean(isCurrent());

    measure(key, () => {
      if (!current()) return;
      const elements = Array.from(targets() || []);
      const stops = elements.map((element) => needsStop(element));

      write(() => {
        if (!current()) return;
        const live = new Set(targets() || []);
        elements.forEach((element, index) => {
          if (!live.has(element)) return;
          const stop = stops[index];
          if (stop === (element.getAttribute('tabindex') === '0')) return;
          if (stop) element.setAttribute('tabindex', '0');
          else element.removeAttribute('tabindex');
        });
      });
    });
  }

  window.DieuxFrame = Object.freeze({
    read,
    write,
    measure,
    cancelMeasure,
    resolveLength,
    resolveCustomProperty,
    reserveTailRoom,
    afterPaint,
    motionQuery,
    prefersReducedMotion,
    focusableSelector,
    tabbableSelector,
    isTabbable,
    hasTabbable,
    syncTabStops,
  });

  window.dispatchEvent(new CustomEvent('dieux:frame:ready'));
})();
